import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Message Offset
 *
 * Use this hook to retrieve the distance between the top of an ancestor and the
 * top or bottom of an element, often a message.
 *
 * Returns `messageRef`, the ref to be attached to the message element, and
 * `messageOffset`, the distance in pixels from the top of the ancestor to the
 * specified edge of the message element.
 */
export const useMessageOffset = ({
  relativeToRef,
  edge = 'bottom',
  timeout = 700,
}: {
  relativeToRef: React.RefObject<HTMLElement>,
  edge?: 'top' | 'bottom',
  timeout?: number,
}) => {
  const observerRef = useRef<ResizeObserver | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [messageOffset, setMessageOffset] = useState(0);

  const computeOffset = useCallback(() => {
    // Clear any pending computation
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      const element = elementRef.current;
      const relativeTo = relativeToRef.current;

      if (!element || !relativeTo) {
        setMessageOffset(0);
        return;
      }

      let offsetTop = 0;
      let currentAncestor: HTMLElement = element;

      // Keep adding offsetTop until we reach the exchange element
      while (currentAncestor && currentAncestor !== relativeTo) {
        offsetTop += currentAncestor.offsetTop;
        currentAncestor = currentAncestor.offsetParent as HTMLElement;
      }

      // If we asked for distance to the bottom edge, add the element's height
      // to the offset
      const distance = edge === 'bottom' ? offsetTop + element.offsetHeight : offsetTop;

      setMessageOffset(distance);
    }, timeout);
  }, [edge, relativeToRef, timeout]);

  const messageRef = useCallback((el: HTMLDivElement | null) => {
    // keep a pointer to the element for comparisons
    elementRef.current = el;

    // Disconnect any previous resize observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!el) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      setMessageOffset(0);
      return;
    }

    /** Compute distance from top of finalExchange to bottom of curve element */

    // Immediate measurement
    computeOffset();

    // Observe future size changes on the curve element and the final exchange (if present)
    const computeObserver = new ResizeObserver(() => {
      computeOffset();
    });

    computeObserver.observe(el);

    if (relativeToRef.current) {
      computeObserver.observe(relativeToRef.current);
    }

    observerRef.current = computeObserver;
  }, [computeOffset, relativeToRef]);

  // Clean up observers and timers on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return { messageRef, messageOffset, computeOffset };
};
